// Kalendář trenéra (29. 9. 2026): stejná mřížka jako Kalendář týmu (KalendarTymu),
// jen s vlastním sloupcem. Klik na bílé pole založí trénink, klik na blok otevře
// detail, jméno klientky otevře její kartu nad kalendářem. Panely Pravidelné
// termíny, Hodiny pro klientky a iCal zůstávají, jako tlačítka nad kalendářem.
//
// Používá se pro trenéra (/trener/kalendar) i pro manažera a CSM
// (/manager/treneri/kalendar, výběr trenéra). Starý tabulkový kalendář
// (KalendarTrenera v Kalendar.jsx) zůstává v souboru jen pro případ návratu.
import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Calendar, Clock, Plus, RefreshCw, Repeat, Share2 } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { EmptyState, SkeletonCards, SkeletonRows, Hlaska } from "../../components/ui.jsx";
import { useApiData } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import { useActiveTrainers, useStickyTrener, useTreneriInfo, TrainerSelectBar } from "../manager/TrenerRozvrh.jsx";
import KalendarTymu from "../rezervace/KalendarTymu.jsx";
import { ModalObal, SeriePanel, HodinyPanel, IcalPanel, SerieCekaji, zpracujNavratGcal } from "./Kalendar.jsx";

function pragueToday() { const d = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" })); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }

export default function TrenerKalendar() {
  const { activeTrener, isOwner } = useOutletContext() || {};
  const { data: muj, loading: nacitaPravo } = useApiData(
    activeTrener ? (isOwner ? `/api/my-trainer?jmeno=${encodeURIComponent(activeTrener)}` : "/api/my-trainer") : null
  );
  // FW-151 (15. 10. 2026): vedoucí lokality (Head Coach Junior, vede_lokalitu) si tu přepne
  // na kalendář kolegy ze své lokality. Výběr byl ve starém kalendáři (Kalendar.jsx) a při
  // přepisu 29. 9. se sem nepřenesl, proto ho Michaela neviděla. Seznam i příznak dává server
  // (akce moji_treneri) stejným pravidlem, jakým pouští ke kalendáři. Owner v náhledu
  // dostane seznam za náhledového trenéra (pro=), takže vidí přesně to, co trenér.
  const { data: mojiTreneri } = useApiData(
    activeTrener
      ? (isOwner ? `/api/rezervace?akce=moji_treneri&pro=${encodeURIComponent(activeTrener)}` : "/api/rezervace?akce=moji_treneri")
      : null
  );
  const kolegove = useMemo(() => (mojiTreneri?.treneri || [])
    .filter((t) => !t.nutricni && t.display_name && t.display_name !== activeTrener)
    .map((t) => t.display_name)
    .sort((a, b) => a.localeCompare(b, "cs")), [mojiTreneri, activeTrener]);
  const vedeLokalitu = !!String(mojiTreneri?.vede_lokalitu || "").trim() && kolegove.length > 0;
  const infoKoleg = useMemo(() => Object.fromEntries((mojiTreneri?.treneri || []).map((t) => [t.display_name, { lokalita: t.lokalita || "", foto: t.foto || "" }])), [mojiTreneri]);
  const [vybrany, setVybrany] = useState("");
  useEffect(() => { setVybrany(""); }, [activeTrener]);
  if (!activeTrener) return <div className="text-sm text-slate-400">Vyber trenéra.</div>;
  if (nacitaPravo && !muj) return <SkeletonCards count={1} />;
  if (muj && muj.rezervace_kalendar !== true) {
    return <EmptyState icon={Calendar} title="Kalendář rezervací zatím není zapnutý" hint="Tréninky zatím řeš v Reserviu. Kalendář se trenérům zapíná postupně, ozveme se, až přijde řada na tebe." />;
  }
  if (!vedeLokalitu) return <KalendarJednohoTrenera trener={activeTrener} key={activeTrener} />;
  const zobrazeny = vybrany && kolegove.includes(vybrany) ? vybrany : activeTrener;
  return (
    <div className="space-y-4">
      <TrainerSelectBar value={zobrazeny} onChange={(v) => setVybrany(!v || v === activeTrener ? "" : v)} trainers={[activeTrener, ...kolegove]} info={infoKoleg} />
      <KalendarJednohoTrenera trener={zobrazeny} key={zobrazeny} />
    </div>
  );
}

export function ManagerKalendar() {
  // 15. 10. 2026 (Láďa): i vedení, které trénuje (Michal Šulc).
  const trainers = useActiveTrainers({ vcetneVedeni: true });
  const info = useTreneriInfo();
  const [trener, setTrener] = useStickyTrener(trainers);
  return (
    <div className="space-y-4">
      <TrainerSelectBar value={trener} onChange={setTrener} trainers={trainers} info={info} />
      {!trener && <div className="text-sm text-slate-400">Vyber trenéra pro zobrazení kalendáře.</div>}
      {trener && <KalendarJednohoTrenera trener={trener} key={trener} />}
    </div>
  );
}

// `trener` je display_name (tak ho vede TrenerLayout i výběr manažera); id, série
// a pracovní okna dodá týden z /api/rezervace.
// 2. 10. 2026: `bezHlavicky` pro vložení do stránky Rezervace (manažer, který sám
// trénuje, má tam pohled Moje tréninky), tlačítka + a Obnovit pak dodá ta stránka
// přes `novyZvenku`, `onNovyZavrit` a `obnovZvenku`.
export function KalendarJednohoTrenera({ trener, bezHlavicky = false, novyZvenku = false, onNovyZavrit, obnovZvenku = 0 }) {
  const user = getCurrentUser();
  const dnes = pragueToday();
  const { data, refetch, loading, error } = useApiData(`/api/rezervace?akce=tyden&trener=${encodeURIComponent(trener)}&od=${dnes}&do=${dnes}`, { bezKese: true });
  const [obnov, setObnov] = useState(0);
  const [novy, setNovy] = useState(false);
  useEffect(() => { if (obnovZvenku) refetch(); }, [obnovZvenku]); // eslint-disable-line react-hooks/exhaustive-deps
  const [panel, setPanel] = useState(null); // 'serie' | 'hodiny' | 'ical'
  // 25. 10. 2026: po návratu z propojení Google kalendáře rovnou otevřít panel Kalendář v telefonu.
  useEffect(() => { if (zpracujNavratGcal()) setPanel("ical"); }, []);
  const [confirm, setConfirm] = useState(null);
  const obnovit = () => { refetch(); setObnov((n) => n + 1); };
  if (error) return <Hlaska ton="problem" title="Kalendář se nepodařilo načíst">{String(error.message || error)}</Hlaska>;
  if (loading && !data) return <SkeletonRows rows={8} />;
  if (!data?.trener) return null;
  const smiOkna = ["owner", "manager", "csm"].includes(String(user?._realRole || user?.role || ""));
  const panelObsah = panel === "serie" ? <SeriePanel data={data} trener={trener} onChange={obnovit} setConfirm={setConfirm} onNova={() => { setPanel(null); setNovy(true); }} />
    : panel === "hodiny" ? <HodinyPanel data={data} trener={trener} onChange={obnovit} smiEditovat={smiOkna} />
    : panel === "ical" ? <IcalPanel trener={trener} /> : null;
  const panelTitulek = { serie: "Pravidelné termíny", hodiny: "Tréninkové hodiny", ical: "Kalendář v telefonu" }[panel];
  const panelIkona = { serie: <Repeat className="w-5 h-5" />, hodiny: <Clock className="w-5 h-5" />, ical: <Share2 className="w-5 h-5" /> }[panel];
  // 1. 10. 2026: na mobilu tři stejně široké dlaždice v jednom řádku (Pravidelné,
  // Dostupnost, Kalendář), na počítači čipy s celými názvy. Okno má vždy celý název.
  const dlazdice = [
    { klic: "serie", Ikona: Repeat, kratky: "Pravidelné", dlouhy: "Pravidelné termíny", pocet: data.serie?.length || 0 },
    { klic: "hodiny", Ikona: Clock, kratky: "Dostupnost", dlouhy: "Tréninkové hodiny", pocet: 0 },
    { klic: "ical", Ikona: Share2, kratky: "Kalendář", dlouhy: "Kalendář v telefonu", pocet: 0 },
  ];
  const doplnky = (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2 sm:hidden">
        {dlazdice.map(({ klic, Ikona, kratky, dlouhy, pocet }) => (
          <button key={klic} type="button" onClick={() => setPanel(klic)} aria-label={dlouhy}
            className="relative flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-2xl border border-slate-200 bg-white text-xs font-bold text-brand-700 transition active:bg-brand-50">
            <Ikona className="h-5 w-5" />
            {kratky}
            {pocet ? <span className="absolute right-2 top-1.5 rounded-full bg-brand px-1.5 text-[11px] font-bold leading-4 text-white">{pocet}</span> : null}
          </button>
        ))}
      </div>
      <div className="hidden flex-wrap gap-2 sm:flex">
        {dlazdice.map(({ klic, Ikona, dlouhy, pocet }) => (
          <button key={klic} type="button" className="cip" onClick={() => setPanel(klic)}><Ikona className="h-4 w-4" />{dlouhy}{pocet ? <span className="rounded-full bg-brand px-1.5 text-[11px] text-white">{pocet}</span> : null}</button>
        ))}
      </div>
      {data.rezim !== "vlastni" && <span className="inline-flex items-center rounded-xl border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-700">Zkušební provoz: tréninky z Reservia se tu jen zobrazují, co založíš tady, v Reserviu není.</span>}
    </div>
  );
  return (
    <div className="space-y-4 sm:space-y-6">
      {!bezHlavicky && <GradientHeader
        title="Kalendář"
        icon={Calendar}
        napoveda="Bílé pole je volno v tvých hodinách, klik založí trénink (osobní, zkušební, školení). Druhá klientka ve stejnou hodinu je spojený trénink. Klik na trénink otevře detail s přesunem a zrušením, jméno klientky otevře její kartu."
        akce={[
          { klic: "novy", ikona: Plus, popis: "Nová rezervace", onClick: () => setNovy(true) },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: obnovit },
        ]}
      />}
      <SerieCekaji data={data} onChange={obnovit} />
      <KalendarTymu user={user} obnov={obnov + obnovZvenku} novy={novy || novyZvenku} onNovyZavrit={() => { setNovy(false); onNovyZavrit?.(); refetch(); }} trener={data.trener} doplnky={doplnky} />
      {panel && <ModalObal title={panelTitulek} icon={panelIkona} onClose={() => setPanel(null)}><div className="-m-4">{panelObsah}</div></ModalObal>}
      <ConfirmDialog open={!!confirm} title={confirm?.title || ""} description={confirm?.description} confirmLabel={confirm?.confirmLabel || "Potvrdit"} variant={confirm?.variant || "danger"} onConfirm={() => { const fn = confirm?.onConfirm; setConfirm(null); fn?.(); }} onCancel={() => setConfirm(null)} />
    </div>
  );
}
